import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { History, Loader2, RotateCcw, GitCompare, Layers } from 'lucide-react';
import { SOURCE_META } from './VersionHistoryDrawer';
import { AdminTextDiffDialog } from './AdminTextDiffDialog';

/** Chapter History panel for the admin Editorial review tool
 * (EditorReviewWorkspace.jsx) — same idea as the Writer's own
 * VersionHistoryDrawer, but talks to the admin-token-gated
 * /editor/reviews/{reviewId}/chapters/{chapterId}/versions(...) routes
 * instead of the regular Loom-user /chapters/{cid}/versions ones,
 * since this tool uses a separate auth system. */
export function EditorChapterHistoryDrawer({ open, onClose, http, reviewId, chapterId, onRestored }) {
  const [loading, setLoading]   = useState(false);
  const [versions, setVersions] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [compare, setCompare]   = useState(null); // {older, newer} version pair

  const load = useCallback(async () => {
    if (!chapterId) return;
    setLoading(true);
    try {
      const r = await http.get(`/editor/reviews/${reviewId}/chapters/${chapterId}/versions`);
      setVersions(r.data.versions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load chapter history.');
    } finally { setLoading(false); }
  }, [http, reviewId, chapterId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (version) => {
    if (!window.confirm(
      `Restore chapter to version ${version.version_number}? The current text will be snapshotted first, so this can be undone.`,
    )) return;
    setRestoringId(version.id);
    try {
      const r = await http.post(
        `/editor/reviews/${reviewId}/chapters/${chapterId}/versions/${version.id}/restore`,
      );
      toast.success(`Restored to version ${version.version_number}.`);
      onRestored?.(r.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not restore this version.');
    } finally { setRestoringId(null); }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="ed-history-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-[14px]">
              <History className="h-4 w-4 text-primary" /> Chapter history
            </SheetTitle>
            <SheetDescription className="text-[13.5px]">
              Every AI generation, manual edit, and editor direct-commit snapshot for this chapter — restore any of them.
            </SheetDescription>
          </SheetHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <div className="py-10 text-center text-[13.5px] text-muted-foreground" data-testid="ed-history-empty">
              No saved versions yet for this chapter.
            </div>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="ed-history-list">
              {versions.map((v, idx) => {
                const meta = SOURCE_META[v.source] || { label: v.source, Icon: Layers, cls: 'text-muted-foreground' };
                const Icon = meta.Icon;
                const older = versions[idx + 1];
                return (
                  <li key={v.id} data-testid={`ed-history-item-${v.id}`}
                      className="rounded-lg border border-border bg-background p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-[13.5px] font-semibold">
                        <Icon className={`h-3.5 w-3.5 ${meta.cls}`} /> v{v.version_number} · {meta.label}
                      </div>
                      <span className="text-[13px] text-muted-foreground">
                        {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    {v.note && (
                      <p className="mt-1 text-[13px] text-muted-foreground line-clamp-2">{v.note}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {older && (
                        <Button size="sm" variant="outline" className="h-6 gap-1 text-[13px]"
                                data-testid={`ed-history-compare-${v.id}`}
                                onClick={() => setCompare({ older, newer: v })}>
                          <GitCompare className="h-3 w-3" /> Compare to previous
                        </Button>
                      )}
                      <Button size="sm" className="h-6 gap-1 bg-emerald-600 text-[13px] text-white hover:bg-emerald-700"
                              data-testid={`ed-history-restore-${v.id}`}
                              disabled={restoringId === v.id}
                              onClick={() => restore(v)}>
                        {restoringId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        Restore
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      <AdminTextDiffDialog
        open={!!compare}
        onClose={() => setCompare(null)}
        title={compare ? `Version ${compare.older.version_number} → ${compare.newer.version_number}` : ''}
        leftLabel={compare ? `v${compare.older.version_number}` : 'Before'}
        rightLabel={compare ? `v${compare.newer.version_number}` : 'After'}
        leftText={compare?.older?.content || ''}
        rightText={compare?.newer?.content || ''}
      />
    </>
  );
}
